import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { STATUS_STYLES, rowTint, fmtDate } from "@/lib/helpers";
import { Plus, Trash2, Upload, FileDown, CalendarDays } from "lucide-react";

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
import { toast } from "sonner";

const empty = { assigned_to: "", project: "", description: "", priority: "Medium", deadline: "", target: 0, status: "Pending", remarks: "" };

export default function TaskAllocation() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks"], queryFn: async () => (await api.get("/tasks")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data, enabled: isAdmin });
  const empName = (id) => employees.find((e) => e.employee_id === id)?.name || id;

  const create = useMutation({
    mutationFn: () => api.post("/tasks", { ...form, target: Number(form.target) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Task assigned — email sent"); setOpen(false); setForm(empty); },
    onError: () => toast.error("Failed to create task"),
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => api.put(`/tasks/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Task updated"); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/tasks/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Task deleted"); },
  });

  const [importOpen, setImportOpen] = useState(false);
  const [importEmp, setImportEmp] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importPriority, setImportPriority] = useState("Medium");
  const [importDeadline, setImportDeadline] = useState("");
  const [importTarget, setImportTarget] = useState("");
  const downloadSource = async (importId, name) => {
    try {
      const res = await api.get(`/tasks/import-file/${importId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name || "tasks.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the Excel file"); }
  };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/tasks/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "VNS_Task_Template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };
  const submitImport = async () => {
    if (!importEmp) { toast.error("Select an employee to assign the tasks to"); return; }
    if (!importFile) { toast.error("Choose an Excel (.xlsx) file"); return; }
    const fd = new FormData(); fd.append("file", importFile); fd.append("assigned_to", importEmp);
    fd.append("priority", importPriority); fd.append("deadline", importDeadline);
    if (importTarget !== "") fd.append("target", String(parseInt(importTarget, 10)));
    try {
      const res = await api.post("/tasks/import", fd);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      const who = employees.find((e) => e.employee_id === importEmp)?.name || "employee";
      toast.success(`Excel assigned to ${who} as 1 task (${res.data.rows} row(s) inside)`);
      setImportOpen(false); setImportEmp(""); setImportFile(null); setImportPriority("Medium"); setImportDeadline(""); setImportTarget("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
  };

  return (
    <div data-testid="task-allocation">
      <PageHeader title="Daily Task Allocation" subtitle="Assign, track and update task progress">
        {isAdmin && (
          <>
            <Button variant="outline" onClick={downloadTemplate} data-testid="task-template-btn" className="rounded-lg"><FileDown className="mr-2 h-4 w-4" /> Template</Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="task-import-btn" className="rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50"><Upload className="mr-2 h-4 w-4" /> Import Excel</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Plus className="mr-2 h-4 w-4" /> Assign Task</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-navy">Assign New Task</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Assign To</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                    <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Project</Label><Input data-testid="task-project-input" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} /></div>
                <div><Label>Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
                <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Deadline</Label><Input type="date" data-testid="task-deadline-input" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={() => create.mutate()} data-testid="save-task-btn" className="bg-navy text-white">Assign</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        )}
      </PageHeader>

      {isAdmin && (
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-navy">Assign Excel as a Task</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Upload one Excel file — it is assigned to the chosen employee as a <b>single task</b> (the whole sheet, not one task per row). The employee can open the attached Excel from the task.</p>
              <div>
                <Label>Assign this Excel to</Label>
                <Select value={importEmp} onValueChange={setImportEmp}>
                  <SelectTrigger data-testid="import-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Excel file (.xlsx)</Label>
                <Input type="file" accept=".xlsx" data-testid="import-file-input" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
                {importFile && <p className="mt-1 text-xs text-emerald-700">{importFile.name}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={importPriority} onValueChange={setImportPriority}>
                    <SelectTrigger data-testid="import-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Target</Label><Input type="number" data-testid="import-target-input" value={importTarget} onChange={(e) => setImportTarget(e.target.value)} placeholder="auto = rows" /></div>
              </div>
              <div>
                <Label>Deadline</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="import-deadline-trigger"
                      className="w-full justify-start rounded-lg font-normal text-left"
                    >
                      <CalendarDays className="mr-2 h-4 w-4 text-slate-500" />
                      {importDeadline ? fmtDate(importDeadline) : <span className="text-slate-400">Pick a deadline</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      data-testid="import-deadline-calendar"
                      selected={importDeadline ? new Date(importDeadline) : undefined}
                      onSelect={(d) => setImportDeadline(d ? toYMD(d) : "")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="rounded-lg"><FileDown className="mr-2 h-4 w-4" /> Template</Button>
              <Button onClick={submitImport} data-testid="import-submit-btn" className="bg-navy text-white">Import &amp; Assign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <SectionCard testId="task-table-card">
        {isLoading ? <Skeleton className="h-64" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Assigned To", "Project", "Description", "Priority", "Deadline", "Target", "Status", ""].map((h) => (
                    <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((t) => (
                  <TableRow key={t.task_id} data-testid={`task-row-${t.task_id}`} className={rowTint(t.status, t.overdue)}>
                    <TableCell className="font-medium">{empName(t.assigned_to)}</TableCell>
                    <TableCell>{t.project}</TableCell>
                    <TableCell className="max-w-xs text-slate-500">
                      <div className="truncate whitespace-pre-line">{t.description}</div>
                      {t.import_id && (
                        <button
                          data-testid={`download-excel-${t.task_id}`}
                          onClick={() => downloadSource(t.import_id, t.source_file)}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
                        >
                          <FileDown className="h-3.5 w-3.5" /> View source Excel
                        </button>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[t.priority]}>{t.priority}</Badge></TableCell>
                    <TableCell className={t.overdue ? "text-red-700 font-semibold" : ""}>{fmtDate(t.deadline)}{t.overdue && " ⚠"}</TableCell>
                    <TableCell>{t.target}</TableCell>
                    <TableCell>
                      <Select value={t.status} onValueChange={(v) => update.mutate({ id: t.task_id, body: { status: v } })}>
                        <SelectTrigger data-testid={`task-status-${t.task_id}`} className="h-8 w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && <Button variant="ghost" size="icon" onClick={() => del.mutate(t.task_id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
